import { ReactElement, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { bottomNavigatorButtonInfo } from '../../../../constants/bottomNavigator/bottomNavigatorButtonInfo.constant';
import { browserStorageKeys } from '../../../../constants/browserStorageKeys/browserStorageKeys.constant';
import { marketPricePageInfo, newProductPageInfo, pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { useGetNewProduct } from '../../../../hooks/newProduct/useGetNewProduct';
import { checkHasNewProductNotification } from '../../../../utils/checker/checkHasNewProductNotification.util';
import { BottomNavigatorButton } from './BottomNavigatorButton';

interface BottomNavigatorProps {
  children?: ReactElement;
}

export const BottomNavigator = ({ children }: BottomNavigatorProps) => {
  const location = useLocation();
  const { newProduct, newOpenMarketProduct, newUsersOwnProduct } = useGetNewProduct();

  const selectedNum = useMemo(() => {
    if (location.pathname === pageInfo.homePage.path) {
      return 0;
    }
    if (location.pathname === pageInfo.productListPage.path) {
      return 1;
    }
    if (location.pathname === pageInfo.openMarketListPage.path) {
      return 2;
    }
    // if (location.pathname === newProductPageInfo.listPage.path) {
    //   return 3;
    // }
    if (location.pathname === marketPricePageInfo.listPage.path) {
      return 3;
    }
    if (location.pathname === pageInfo.pluvaAutoPage.path) {
      return 4;
    }

    return 5;
  }, [location.pathname]);

  // 마켓 알림 뱃지 여부
  const hasNewProductNotification = useMemo((): boolean => {
    if (location.pathname.includes(pageInfo.productListPage.path)) {
      return false;
    }

    return checkHasNewProductNotification({
      id: newProduct?.id,
      startOnSaleDate: newProduct?.startOnSaleDate,
      key: browserStorageKeys.newMarketProductInfo,
    });
  }, [newProduct, location.pathname]);

  // 오픈마켓 알림 뱃지 여부
  const hasNewDirectDealProductNotification = useMemo((): boolean => {
    if (location.pathname.includes(pageInfo.openMarketListPage.path)) {
      return false;
    }

    const hasNewOpenMarketProduct = checkHasNewProductNotification({
      id: newOpenMarketProduct?.id,
      startOnSaleDate: newOpenMarketProduct?.approvalDate,
      key: browserStorageKeys.newOpenMarketProductInfo,
      days: 3, // 오픈마켓은 3일
    });
    return hasNewOpenMarketProduct;
  }, [newOpenMarketProduct, location.pathname]);

  const hasNewPokingProductNotification = useMemo((): boolean => {
    if (location.pathname.includes(pageInfo.tractorPokingListPage.path)) {
      return false;
    }

    const hasNewUserOwnProduct = checkHasNewProductNotification({
      id: newUsersOwnProduct?.id,
      startOnSaleDate: newUsersOwnProduct?.registeredAt,
      key: browserStorageKeys.newUsersOwnProductInfo,
    });

    return hasNewUserOwnProduct;
  }, [newUsersOwnProduct, location.pathname]);

  const hasBottomNavigatorPathnameList = [
    pageInfo.infoListPage.path,
    pageInfo.infoDetailPage.path,
    pageInfo.homePage.path,
    pageInfo.productListPage.path,
    pageInfo.openMarketListPage.path,
    marketPricePageInfo.listPage.path,
    pageInfo.tractorPokingListPage.path,
    pageInfo.myInfoPage.path,
    pageInfo.loginPage.path,
    pageInfo.cropTrendsListPage.path,
    newProductPageInfo.listPage.path,
    pageInfo.subsidyListPage.path,
    pageInfo.subsidyDetailPage.path,
  ];

  const isShow = useMemo<boolean>(() => {
    let hasBottomNavigator = false;

    // URL이 정확히 일치할 때
    hasBottomNavigatorPathnameList.forEach((pathname) => {
      if (location.pathname.includes(pathname)) {
        hasBottomNavigator = true;
      }
    });
    return hasBottomNavigator;
  }, [location.pathname]);

  return (
    <Container id={'bottom-navigation'} className={'backdrop-blur-sm'} isShow={isShow}>
      <TabList id={'tabs'}>
        {bottomNavigatorButtonInfo.map((navButton, index) => {
          let hasNotification = false;

          if (navButton.link === pageInfo.productListPage.path) {
            hasNotification = hasNewProductNotification;
          }

          if (navButton.link === pageInfo.openMarketListPage.path) {
            hasNotification = hasNewDirectDealProductNotification;
          }

          if (navButton.link === pageInfo.tractorPokingListPage.path) {
            hasNotification = hasNewPokingProductNotification;
          }

          const defaultBottomNavigatorButtonProps = {
            key: navButton.buttonTitle,
            link: navButton.link,
            isSelected: selectedNum === index,
            title: navButton.buttonTitle,
            defaultIcon: navButton.defaultIcon,
            pressedIcon: navButton.pressedIcon,
            isExternalLink: navButton.isExternalLink,
            hasNotification,
          };
          return <BottomNavigatorButton {...defaultBottomNavigatorButtonProps} />;
        })}
      </TabList>
      {children}
    </Container>
  );
};

const Container = styled.section<{ isShow: boolean }>`
  display: ${(props) => (props.isShow ? 'block' : 'none')};

  width: 100%;
  max-width: 512px;

  background: #ffffff;
  backdrop-filter: blur(8px);

  border-bottom: none;

  position: relative;
  z-index: 100;
`;

const TabList = styled.ul`
  width: 100%;

  display: flex;
  justify-content: space-between;
`;
